import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { resolveMediaUrl } from "../lib/api";
import TikTokIcon from "./TikTokIcon";

/**
 * Horizontal scrollable product row.
 * Used for TikTok Virals, Recent Products, Niche selections.
 */
const HorizontalProductRow = ({
  eyebrow,
  title,
  emphasis,
  description,
  products = [],
  seeAllHref,
  seeAllLabel = "Tout voir",
  showTikTokBadge = false,
  testId = "product-row",
}) => {
  const { formatPrice } = useCurrency();
  const scrollerRef = useRef(null);

  const scroll = (direction) => {
    const s = scrollerRef.current;
    if (!s) return;
    s.scrollBy({ left: direction * 340, behavior: "smooth" });
  };

  if (!products || products.length === 0) return null;

  return (
    <section data-testid={testId} className="py-20 md:py-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <div>
            {eyebrow && (
              <p className="label-caps text-[color:var(--gold)] mb-4 flex items-center gap-2">
                {showTikTokBadge && <TikTokIcon size={14} className="text-[color:var(--gold)]" />}
                {eyebrow}
              </p>
            )}
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              {title} {emphasis && <em className="italic text-[color:var(--gold-soft)]">{emphasis}</em>}
            </h2>
            {description && (
              <p className="text-white/50 font-light mt-4 max-w-xl text-sm md:text-base">{description}</p>
            )}
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll(-1)}
              data-testid={`${testId}-scroll-left`}
              aria-label="Précédent"
              className="w-11 h-11 border border-white/20 hover:border-[color:var(--gold)] flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => scroll(1)}
              data-testid={`${testId}-scroll-right`}
              aria-label="Suivant"
              className="w-11 h-11 border border-white/20 hover:border-[color:var(--gold)] flex items-center justify-center transition-colors"
            >
              <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-5 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          {products.map((p, i) => {
            const onSale = typeof p.sale_price === "number" && p.sale_price > 0 && p.sale_price < p.price_100ml;
            const discountPct = onSale ? Math.round(((p.price_100ml - p.sale_price) / p.price_100ml) * 100) : 0;
            return (
            <Link
              key={p.id}
              to={`/parfum/${p.slug}`}
              data-testid={`${testId}-card-${p.slug}`}
              className="group flex-shrink-0 w-[240px] md:w-[280px] snap-start relative"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#0A0A0A]">
                <img
                  src={resolveMediaUrl(p.image_main)}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                />
                {showTikTokBadge && (
                  <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1.5 text-[9px] label-caps border border-white/10">
                    <TikTokIcon size={11} /> Viral
                  </span>
                )}
                {p.badge && !showTikTokBadge && !onSale && (
                  <span className="absolute top-3 left-3 label-caps text-black bg-[color:var(--gold)] px-2.5 py-1.5 text-[9px]">
                    {p.badge}
                  </span>
                )}
                {onSale && (
                  <span className="absolute top-3 right-3 label-caps bg-red-600 text-white px-2.5 py-1.5 text-[9px] shadow-lg">
                    Solde −{discountPct}%
                  </span>
                )}
              </div>
              <div className="pt-4 pb-2">
                <p className="label-caps text-white/40 text-[10px] mb-1.5">{p.brand || p.category}</p>
                <h3 className="font-serif text-lg md:text-xl text-white group-hover:text-[color:var(--gold)] transition-colors leading-tight">
                  {p.name}
                </h3>
                <div className="flex items-end justify-between mt-2">
                  <p className="text-xs text-white/50 italic truncate max-w-[60%]">{p.tagline}</p>
                  {onSale ? (
                    <p className="whitespace-nowrap flex items-baseline gap-1.5">
                      <span className="font-serif text-base text-red-400">{formatPrice(p.sale_price)}</span>
                      <span className="text-[10px] text-white/40 line-through">{formatPrice(p.price_100ml)}</span>
                    </p>
                  ) : (
                    <p className="font-serif text-base text-[color:var(--gold)] whitespace-nowrap">
                      {formatPrice(p.price_100ml)}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          );})}
        </div>

        {seeAllHref && (
          <div className="text-center mt-12">
            <Link to={seeAllHref} data-testid={`${testId}-see-all`} className="btn-outline">
              {seeAllLabel} <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default HorizontalProductRow;
